import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";
import type { VerificationQueueItem, VerificationStatus } from "@/admin/types/admin";

export function useVerifications(statusFilter?: VerificationStatus) {
  const queryClient = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["admin-verifications", statusFilter],
    queryFn: async (): Promise<VerificationQueueItem[]> => {
      const { data, error } = await supabase.rpc("admin_get_verifications" as any, {
        p_status: statusFilter ?? null,
      });
      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        artisanProfileId: row.artisan_profile_id,
        userId: row.user_id,
        fullName: row.display_name ?? row.email ?? "Inconnu",
        email: row.email ?? "",
        specialty: row.specialite ?? "",
        wilaya: row.wilaya ?? "",
        verificationStatus: row.verification_status as VerificationStatus,
        verificationRequestedAt: row.verification_requested_at,
        verifiedAt: row.verified_at,
        rejectionReasonCode: row.rejection_reason_code,
        verificationNotes: row.verification_notes,
        avatarUrl: row.avatar_url ?? null,
      }));
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (artisanProfileId: string) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("artisan_profiles")
        .update({
          verification_status: "verified",
          verified_at: now,
          verified_by_admin_id: adminUserId,
        })
        .eq("id", artisanProfileId);
      if (error) throw error;
      return { artisanProfileId, now };
    },
    onSuccess: async ({ artisanProfileId }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "artisan_verified",
          targetType: "artisan_profile",
          targetId: artisanProfileId,
        });
      }
      toast.success("Artisan vérifié avec succès");
      queryClient.invalidateQueries({ queryKey: ["admin-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: () => toast.error("Erreur lors de la vérification"),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({
      artisanProfileId,
      reasonCode,
      notes,
    }: {
      artisanProfileId: string;
      reasonCode: string;
      notes?: string;
    }) => {
      const { error } = await supabase
        .from("artisan_profiles")
        .update({
          verification_status: "rejected",
          rejection_reason_code: reasonCode,
          verification_notes: notes ?? null,
          verified_by_admin_id: adminUserId,
        })
        .eq("id", artisanProfileId);
      if (error) throw error;
      return { artisanProfileId, reasonCode, notes };
    },
    onSuccess: async ({ artisanProfileId, reasonCode, notes }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "artisan_rejected",
          targetType: "artisan_profile",
          targetId: artisanProfileId,
          reasonCode,
          notes,
        });
      }
      toast.success("Demande de vérification rejetée");
      queryClient.invalidateQueries({ queryKey: ["admin-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: () => toast.error("Erreur lors du rejet"),
  });

  return { query, approveMutation, rejectMutation };
}
